import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { DeleteOutline, Edit } from "@mui/icons-material";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import SelectField from "../../atoms/SelectField/SelectField";
import {
  createModule,
  deleteModule,
  findAllModules,
  updateModule,
} from "../../../API/apiModules";
import { SaveSystemModulePayload, SystemModule } from "../../../types/SystemModule";
import { useAuth } from "../../../resources/AuthProvider";
import styles from "./GestaoModulos.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";

const STATUS_OPTIONS = [
  { nome: "Todos" },
  { nome: "Ativos" },
  { nome: "Inativos" },
];

const INITIAL_FORM = {
  key: "",
  name: "",
  description: "",
  order: "",
  active: true,
};

const normalizeKey = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");

const GestaoModulos = () => {
  const { userPageList } = useAuth();
  const canAccessForm = userPageList.includes("modulos");

  const [modules, setModules] = useState<SystemModule[]>([]);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("Todos");
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [selectedModule, setSelectedModule] = useState<SystemModule | null>(null);
  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    loadModules();
  }, [activeFilter]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const activeParam = useMemo(() => {
    if (activeFilter === "Ativos") return true;
    if (activeFilter === "Inativos") return false;
    return undefined;
  }, [activeFilter]);

  const loadModules = async (nextSearch = search) => {
    setLoading(true);
    try {
      const response = await findAllModules({
        active: activeParam,
        search: nextSearch.trim() || undefined,
      });
      setModules(response.data || []);
    } catch (error) {
      setMsgError("Não foi possível listar os módulos.");
    } finally {
      setLoading(false);
    }
  };

  const closeForm = () => {
    setOpenForm(false);
    setSelectedModule(null);
    setForm(INITIAL_FORM);
  };

  const openCreateForm = () => {
    setSelectedModule(null);
    setForm(INITIAL_FORM);
    setOpenForm(true);
  };

  const openEditForm = (module: SystemModule) => {
    setSelectedModule(module);
    setForm({
      key: module.key || "",
      name: module.name || "",
      description: module.description || "",
      order: module.order?.toString() || "",
      active: Boolean(module.active),
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.name.trim()) {
      setMsgError("Preencha chave e nome.");
      return;
    }

    try {
      setLoadingSave(true);
      const payload: SaveSystemModulePayload = {
        key: form.key.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        active: form.active,
        order: form.order.trim() ? Number(form.order) : null,
      };

      if (selectedModule) {
        await updateModule(selectedModule.id, payload);
        setMsgSuccess("Módulo atualizado com sucesso.");
      } else {
        await createModule(payload);
        setMsgSuccess("Módulo criado com sucesso.");
      }

      closeForm();
      await loadModules();
    } catch (error) {
      setMsgError("Não foi possível salvar o módulo.");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedModule) return;

    try {
      setLoadingDelete(true);
      await deleteModule(selectedModule.id);
      setMsgSuccess("Módulo excluído com sucesso.");
      setOpenDelete(false);
      setSelectedModule(null);
      await loadModules();
    } catch (error) {
      setOpenDelete(false);
      setMsgError("Não foi possível excluir o módulo.");
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>Gestão de Módulos</div>

        {canAccessForm && (
          <div className={styles.actionsHeader}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              onClick={openCreateForm}
            >
              Novo Módulo
            </Button>
          </div>
        )}

        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <div className={`${styles_grid.grid12} ${styles_general.mb4}`}>
          <div className={styles_grid.inputGroup8}>
            <label className={styles_input.titleInput}>Buscar</label>
            <TextField
              size="small"
              variant="standard"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  loadModules((e.target as HTMLInputElement).value);
                }
              }}
              InputProps={{
                className: styles_input.input,
                disableUnderline: true,
              }}
            />
          </div>

          <div className={styles_grid.inputGroup2}>
            <label className={styles_input.titleInput}>Status</label>
            <SelectField
              label=""
              value={activeFilter}
              onChange={setActiveFilter}
              options={STATUS_OPTIONS}
              disableTyping
              disableClearable
            />
          </div>

          <div className={`${styles_grid.inputGroup2} ${styles_grid.itemsEnd}`}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              onClick={() => loadModules()}
            >
              Filtrar
            </Button>
          </div>
        </div>

        <TableContainer className={styles_table.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={styles_table.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Nome</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Chave</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Descrição</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Páginas</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Status</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {modules.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" className={styles_table.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {modules.map((module, index) => (
                  <TableRow
                    key={module.id}
                    className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={styles_table.textXs}>{module.name}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{module.key}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{module.description || "-"}</TableCell>
                    <TableCell padding="none" align="center" className={styles_table.textXs}>{module.pages_count ?? 0}</TableCell>
                    <TableCell padding="none" align="center" className={styles_table.textXs}>{module.permission_actions_count ?? 0}</TableCell>
                    <TableCell padding="none" align="center" className={styles_table.textXs}>
                      {module.active ? "Ativo" : "Inativo"}
                    </TableCell>
                    <TableCell padding="none">
                      <div className={styles_table.blockActions}>
                        {canAccessForm && (
                          <IconButton size="small" color="primary" onClick={() => openEditForm(module)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedModule(module);
                            setOpenDelete(true);
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </CardContent>

      <Dialog open={openForm} onClose={loadingSave ? undefined : closeForm} maxWidth="md" fullWidth>
        <DialogTitle className={styles.title}>
          {selectedModule ? "Editar Módulo" : "Novo Módulo"}
        </DialogTitle>
        <DialogContent>
          <div className={`${styles_grid.grid12} ${styles_general.mt4}`}>
            <div className={styles_grid.inputGroup3}>
              <label className={styles_input.titleInput}>Chave</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={form.key}
                InputProps={{ className: styles_input.input, disableUnderline: true, readOnly: true }}
              />
            </div>

            <div className={styles_grid.inputGroup5}>
              <label className={styles_input.titleInput}>Nome</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => {
                    const nextName = e.target.value;
                    return {
                      ...prev,
                      name: nextName,
                      key: normalizeKey(nextName),
                    };
                  })
                }
                inputProps={{ maxLength: 100 }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>

            <div className={styles_grid.inputGroup2}>
              <label className={styles_input.titleInput}>Ordem</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={form.order}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, order: e.target.value.replace(/\D/g, "") }))
                }
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>

            <div className={styles_grid.inputGroup10}>
              <label className={styles_input.titleInput}>Descrição</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                multiline
                minRows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                inputProps={{ maxLength: 255 }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>

            <div className={`${styles_grid.inputGroup2} ${styles_grid.itemsEnd}`}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={(_, checked) => setForm((prev) => ({ ...prev, active: checked }))}
                  />
                }
                label={form.active ? "Ativo" : "Inativo"}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className={styles_grid.displayFlexBetween}>
          <Button
            variant="contained"
            onClick={closeForm}
            className={styles_button.buttonSquareSecondary}
            disabled={loadingSave}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            className={`${styles_button.buttonSquarePrimary} ${loadingSave ? styles_button.buttonOpacity : ""}`}
            disabled={loadingSave}
          >
            {loadingSave ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDelete}
        loading={loadingDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Excluir Módulo"
        message={`Deseja realmente excluir o módulo "${selectedModule?.name || ""}"?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />
    </Card>
  );
};

export default GestaoModulos;
